import { createFileRoute, Link } from "@tanstack/react-router";

import { THEMES } from "@/data/themes";
import { useReveal } from "@/hooks/use-reveal";
import { metaDesc } from "@/lib/seo-description";
import { ReviewsProof } from "@/components/reviews-proof";
import { LOCAL_BUSINESS_SCHEMA, absoluteImageUrl } from "@/lib/schema-org";

const URL = "https://dj-val.fr/soiree-karaoke-avignon";
const HERO = THEMES.find((t) => t.slug === "karaoke")?.image ?? THEMES[0].image;

const TITLE = "Soirée karaoké Avignon : animation & DJ | DJ Val";
const DESCRIPTION =
  "Soirée karaoké à Avignon animée par un DJ : 50 000 titres, micros HF, écran de paroles et sono pro. Animation karaoké dans tout le Vaucluse, devis gratuit sous 24 h.";

const PILLARS = [
  {
    title: "50 000 titres FR / EN",
    text: "Un catalogue mis à jour en continu : variété française, tubes internationaux, années 80 à aujourd'hui, chansons de mariage et classiques qui font chanter toute la salle.",
  },
  {
    title: "Micros HF et écran de paroles",
    text: "Deux micros sans fil Shure ou Sennheiser, écran ou vidéoprojecteur pour les paroles, et une sono calibrée pour que les voix passent au-dessus de la musique sans larsen.",
  },
  {
    title: "Un animateur, pas une borne",
    text: "Le karaoké est piloté par un DJ qui relance, enchaîne, gère les timides et coupe au bon moment. C'est ce qui différencie une vraie soirée karaoké d'un écran laissé en libre-service.",
  },
];

const SECTIONS: { h2: string; paragraphs: string[] }[] = [
  {
    h2: "Une soirée karaoké à Avignon clé en main",
    paragraphs: [
      "Organiser une soirée karaoké à Avignon demande plus qu'un micro et une playlist YouTube. Il faut une sonorisation qui sépare correctement les voix de la musique, un écran lisible depuis toute la salle, un catalogue assez large pour que chaque génération y trouve son titre, et surtout quelqu'un pour tenir le rythme de la soirée.",
      "DJ Val installe l'ensemble du dispositif en amont : sono, micros HF, écran de paroles, éclairage d'ambiance. Vous arrivez, vous chantez. À la fin de la soirée, tout est démonté et rechargé sans que vous ayez à intervenir.",
      "L'intervention couvre Avignon et sa périphérie immédiate — Le Pontet, Montfavet, Villeneuve-lès-Avignon, Sorgues, Morières-lès-Avignon — ainsi que l'ensemble du Vaucluse depuis Cavaillon.",
    ],
  },
  {
    h2: "Pour quels événements à Avignon et dans le Vaucluse",
    paragraphs: [
      "Le karaoké est l'animation la plus fédératrice qui existe : elle ne demande aucune compétence, fonctionne dès une quinzaine de personnes et fait participer ceux qui ne dansent jamais. C'est pour cette raison qu'il est demandé sur des contextes très différents.",
      "Anniversaires (18, 30, 40, 50 ans), enterrements de vie de jeune fille et de garçon, soirées d'entreprise et arbres de Noël, repas d'association, fêtes de camping et de village, soirées de bar et de restaurant, fin de soirée de mariage : le format s'adapte à chaque public en ajustant le catalogue et le volume.",
      "Sur un mariage, la formule la plus efficace consiste à placer une séquence karaoké d'une heure après le repas, avant l'ouverture du dancefloor. Les invités montent au micro, l'ambiance décolle, puis la soirée bascule naturellement en mode DJ.",
    ],
  },
  {
    h2: "Animation karaoké ou location de pack : que choisir",
    paragraphs: [
      "Deux formules existent. La première est l'animation karaoké complète : DJ présent toute la soirée, matériel installé et piloté, gestion des inscriptions et de l'enchaînement des chanteurs. C'est la formule à privilégier au-delà de quarante invités ou lorsque le karaoké est le cœur de la soirée.",
      "La seconde est la location de pack karaoké : vous récupérez le matériel — sono, micros, écran, catalogue — et vous animez vous-même. Le retrait est gratuit à Cavaillon et la livraison possible à Avignon et dans tout le Vaucluse. Cette formule convient aux petits comités, aux soirées de camping et aux associations disposant déjà d'un animateur.",
      "Dans les deux cas, le matériel est identique : ce sont les mêmes micros HF et la même sono professionnelle. Seule change la présence de l'animateur.",
    ],
  },
  {
    h2: "Ce que comprend la prestation",
    paragraphs: [
      "La prestation inclut le déplacement sur Avignon, l'installation et le réglage complet, la sonorisation adaptée à la salle, deux micros HF, l'écran de paroles, l'éclairage d'ambiance, l'animation sur toute la durée convenue et le démontage.",
      "DJ Val est déclaré, assuré en responsabilité civile professionnelle et à jour de ses obligations SACEM — un point systématiquement vérifié par les salles municipales et les comités d'entreprise du Vaucluse.",
      "Le devis est transmis sous 24 heures, détaillé poste par poste, sans frais de dossier ni supplément de dernière minute. Un échange téléphonique préalable permet de caler la durée, le nombre d'invités et les contraintes de la salle.",
    ],
  },
];

const FAQ = [
  {
    q: "Combien coûte une soirée karaoké à Avignon ?",
    a: "Une animation karaoké complète avec DJ, sono, micros et écran démarre autour de 390 € pour une soirée d'environ quatre heures à Avignon. Le tarif dépend de la durée, du nombre d'invités et de la salle. Le devis est gratuit et transmis sous 24 h.",
  },
  {
    q: "Combien de titres contient le catalogue karaoké ?",
    a: "Plus de 50 000 titres français et internationaux, des classiques de la variété aux sorties récentes. Vous pouvez envoyer une liste de titres incontournables en amont : ils sont vérifiés et ajoutés avant la soirée.",
  },
  {
    q: "Peut-on faire un karaoké en extérieur ou sous chapiteau ?",
    a: "Oui, à condition de disposer d'une alimentation électrique et d'un abri pour le matériel. Pour l'écran de paroles, une zone ombragée ou une soirée après la tombée de la nuit est nécessaire pour garder une bonne lisibilité.",
  },
  {
    q: "Intervenez-vous en dehors d'Avignon ?",
    a: "Oui. Depuis Cavaillon, l'animation karaoké couvre tout le Vaucluse — Avignon, Carpentras, L'Isle-sur-la-Sorgue, Apt, Orange, Pertuis — ainsi que le nord des Bouches-du-Rhône et le Gard rhodanien.",
  },
  {
    q: "Peut-on louer uniquement le matériel de karaoké ?",
    a: "Oui, un pack karaoké complet est disponible en location seule, avec retrait gratuit à Cavaillon ou livraison à Avignon. Il comprend la sono, deux micros HF, l'écran et l'accès au catalogue.",
  },
];

export const Route = createFileRoute("/soiree-karaoke-avignon")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: metaDesc(DESCRIPTION) },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: metaDesc(DESCRIPTION) },
      { property: "og:image", content: absoluteImageUrl(HERO) },
      { property: "og:url", content: URL },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Soirée karaoké Avignon",
          description: DESCRIPTION,
          serviceType: "Animation karaoké",
          provider: { ...LOCAL_BUSINESS_SCHEMA },
          areaServed: ["Cavaillon", "Avignon", "Vaucluse", "Bouches-du-Rhône", "Gard"],
          url: URL,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: KaraokeAvignonPage,
});

function KaraokeAvignonPage() {
  useReveal();
  return (
    <div className="bg-background text-muted-foreground">
      <section className="on-dark relative min-h-[75vh] flex items-center px-6 overflow-hidden pt-24">
        <div className="absolute inset-0 z-0">
          <img loading="lazy" decoding="async" src={HERO} alt="Soirée karaoké animée par un DJ à Avignon" className="h-full w-full object-cover opacity-40 kenburns-img" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to right, var(--ink) 0%, color-mix(in oklab, var(--ink) 70%, transparent) 55%, color-mix(in oklab, var(--ink) 30%, transparent) 100%), linear-gradient(to bottom, var(--ink) 0%, transparent 40%, var(--ink) 100%)" }} />
        </div>
        <div className="relative z-10 max-w-3xl reveal space-y-6">
          <p className="eyebrow">Animation karaoké · Avignon & Vaucluse</p>
          <h1 className="font-serif-display text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            <span className="italic font-light">Soirée karaoké à Avignon animée par un DJ</span>
          </h1>
          <span className="gold-rule !w-12" />
          <p className="text-base text-foreground/85 font-light leading-relaxed max-w-xl">
            50 000 titres, deux micros HF, écran de paroles et une sono professionnelle : une soirée karaoké à Avignon
            où tout le monde finit par chanter, y compris ceux qui affirmaient le contraire.
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <Link to="/contactez-nous" search={{ sujet: "Devis soirée karaoké Avignon" }} className="inline-block bg-[var(--gold)] text-[var(--ink)] px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all text-center">
              Devis gratuit
            </Link>
            <a href="tel:0767908667" className="text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/90 hover:text-foreground transition-colors underline underline-offset-4 decoration-foreground/30">
              07 67 90 86 67
            </a>
          </div>
        </div>
      </section>
      <ReviewsProof />

      <section className="py-24 px-6 max-w-7xl mx-auto">
        <div className="grid gap-px bg-[var(--gold-line)] md:grid-cols-3 border border-[var(--gold-line)]">
          {PILLARS.map((p, i) => (
            <div key={p.title} className="bg-background p-10 sr" data-reveal style={{ transitionDelay: `${i * 80}ms` }}>
              <p className="font-serif-display italic text-[var(--gold)] text-2xl">{String(i + 1).padStart(2, "0")}</p>
              <h2 className="font-serif-display text-2xl text-foreground mt-4">{p.title}</h2>
              <span className="gold-rule !w-8 my-4" />
              <p className="text-sm text-muted-foreground leading-relaxed font-light">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pb-8 px-6 max-w-4xl mx-auto space-y-14">
        {SECTIONS.map((s) => (
          <div key={s.h2} className="sr" data-reveal>
            <h2 className="font-serif-display text-3xl text-foreground">{s.h2}</h2>
            <span className="gold-rule !w-10 my-5" />
            <div className="space-y-4">
              {s.paragraphs.map((t) => (
                <p key={t.slice(0, 40)} className="text-sm text-muted-foreground leading-relaxed font-light">{t}</p>
              ))}
            </div>
            {s.h2.startsWith("Animation karaoké") && (
              <p className="mt-5 text-sm">
                <Link to="/location-karaoke-vaucluse" className="text-[var(--gold)] underline underline-offset-4">
                  Voir la location de pack karaoké à Avignon et dans le Vaucluse
                </Link>
              </p>
            )}
          </div>
        ))}
      </section>

      <section className="py-24 px-6 max-w-4xl mx-auto">
        <div className="text-center mb-12 sr" data-reveal>
          <p className="eyebrow text-[var(--gold)]">Questions fréquentes</p>
          <h2 className="font-serif-display text-4xl text-foreground mt-4">Soirée karaoké à Avignon</h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
        </div>
        <div className="space-y-4">
          {FAQ.map((f) => (
            <details key={f.q} className="group border border-[var(--gold-line)] bg-card p-6 sr" data-reveal>
              <summary className="cursor-pointer font-serif-display text-xl text-foreground flex justify-between items-center gap-4">
                {f.q}
                <span className="text-[var(--gold)] group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-4 text-sm text-muted-foreground leading-relaxed font-light">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="py-20 px-6 border-t border-[var(--gold-line)] bg-card">
        <div className="max-w-6xl mx-auto text-center">
          <p className="eyebrow text-[var(--gold)]">À découvrir aussi</p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <Link to="/location-karaoke-vaucluse" className="px-5 py-2.5 border border-[var(--gold-line)] text-foreground/85 text-xs uppercase tracking-[0.15em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
              Location karaoké Avignon & Vaucluse
            </Link>
            <Link to="/location-materiel/$slug" params={{ slug: "micros-hf" }} className="px-5 py-2.5 border border-[var(--gold-line)] text-foreground/85 text-xs uppercase tracking-[0.15em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
              Location de micros HF
            </Link>
            <Link to="/dj-mariage/$ville" params={{ ville: "avignon" }} className="px-5 py-2.5 border border-[var(--gold-line)] text-foreground/85 text-xs uppercase tracking-[0.15em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
              DJ mariage Avignon
            </Link>
            <Link to="/soirees-thematiques" className="px-5 py-2.5 border border-[var(--gold-line)] text-foreground/85 text-xs uppercase tracking-[0.15em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
              Soirées thématiques
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
